import { useState, useRef, useEffect, FormEvent } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

export default function Contact() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [charCount, setCharCount] = useState(0);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    document.title = 'Contact | Holston Development Group';
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const children = sectionRef.current?.querySelectorAll('.fade-in-child');
            children?.forEach((child, i) => {
              const el = child as HTMLElement;
              setTimeout(() => {
                el.style.transition = 'opacity 0.7s ease, transform 0.7s ease';
                el.style.opacity = '1';
                el.style.transform = 'translateY(0)';
              }, i * 100);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const message = (form.elements.namedItem('message') as HTMLTextAreaElement)?.value || '';
    if (message.length > 500) return;
    setSubmitting(true);
    const data = new URLSearchParams();
    const fields = ['name', 'email', 'phone', 'company', 'message'];
    fields.forEach((f) => {
      const el = form.elements.namedItem(f) as HTMLInputElement | HTMLTextAreaElement;
      if (el) data.append(f, el.value);
    });
    try {
      await fetch('https://readdy.ai/api/form/d76jn9bq7u9ee9hcegrg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      {/* Hero */}
      <section className="relative pt-32 pb-16 bg-[#001F4E]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 text-center">
          <p className="text-[#00A3E0] text-sm font-semibold uppercase tracking-widest mb-4">Reach Out</p>
          <h1 className="text-4xl lg:text-5xl font-bold text-white">Contact Us</h1>
          <p className="text-white/70 mt-4 text-base max-w-xl mx-auto">
            Ready to discuss your next project? We&apos;re here to help. Reach out and a member of our team will be in touch.
          </p>
        </div>
      </section>

      {/* Form + Info */}
      <section ref={sectionRef} className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14">
            {/* Contact Info */}
            <div>
              <p className="fade-in-child text-[#0050B2] text-sm font-semibold uppercase tracking-widest mb-3" style={{ opacity: 0, transform: 'translateY(20px)' }}>Get In Touch</p>
              <h2 className="fade-in-child text-3xl font-bold text-[#001F4E] mb-6" style={{ opacity: 0, transform: 'translateY(20px)' }}>
                Let&apos;s Start a Conversation
              </h2>
              <p className="fade-in-child text-gray-600 text-base leading-relaxed mb-8" style={{ opacity: 0, transform: 'translateY(20px)' }}>
                Whether you&apos;re exploring a new project, looking for a strategic partner, or have questions about our services, we&apos;d love to hear from you.
              </p>
              <div className="space-y-6">
                {[
                  { icon: 'ri-mail-line', label: 'Email', value: 'admin@holstondevgroup.com', href: 'mailto:admin@holstondevgroup.com' },
                  { icon: 'ri-phone-line', label: 'Phone', value: '(423) 212-5009', href: 'tel:+14232125009' },
                  { icon: 'ri-global-line', label: 'Website', value: 'holstondevgroup.com', href: 'https://holstondevgroup.com' },
                  { icon: 'ri-map-pin-line', label: 'Coverage', value: 'Nationwide Capability', href: undefined },
                ].map((item, i) => (
                  <div key={i} className="fade-in-child flex items-start gap-4" style={{ opacity: 0, transform: 'translateY(20px)' }}>
                    <div className="w-10 h-10 flex items-center justify-center rounded-md bg-[#E8EFF6] shrink-0">
                      <i className={`${item.icon} text-[#0050B2] text-lg`} />
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 font-medium uppercase tracking-wider mb-0.5">{item.label}</p>
                      {item.href ? (
                        <a href={item.href} rel="nofollow" className="text-[#001F4E] font-semibold text-sm hover:text-[#0050B2] cursor-pointer">{item.value}</a>
                      ) : (
                        <p className="text-[#001F4E] font-semibold text-sm">{item.value}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <div className="fade-in-child mt-10 bg-[#E8EFF6] rounded-lg p-6" style={{ opacity: 0, transform: 'translateY(20px)' }}>
                <h3 className="text-[#001F4E] font-bold text-sm mb-2">What to Expect</h3>
                <p className="text-gray-500 text-sm leading-relaxed">
                  After you reach out, a member of our team will review your inquiry and follow up within one business day.
                </p>
              </div>
            </div>

            {/* Form */}
            <div className="fade-in-child bg-[#E8EFF6] rounded-lg p-8" style={{ opacity: 0, transform: 'translateY(20px)' }}>
              {submitted ? (
                <div className="flex flex-col items-center justify-center h-full py-16 text-center">
                  <div className="w-14 h-14 flex items-center justify-center rounded-full bg-[#0050B2] mb-4">
                    <i className="ri-check-line text-white text-2xl" />
                  </div>
                  <h3 className="text-[#001F4E] font-bold text-xl mb-2">Message Sent!</h3>
                  <p className="text-gray-500 text-sm">Thank you for reaching out. A member of our team will be in touch with you shortly.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} data-readdy-form id="contact-form" className="space-y-5">
                  <h3 className="text-[#001F4E] font-bold text-lg mb-1">Send Us a Message</h3>
                  <p className="text-gray-500 text-sm mb-4">Fill out the form below and we&apos;ll respond as soon as possible.</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-[#001F4E] mb-1.5">Full Name <span className="text-red-500">*</span></label>
                      <input name="name" required type="text" placeholder="John Smith" className="w-full px-4 py-2.5 rounded-md border border-white bg-white text-sm text-[#001F4E] placeholder-gray-400 focus:outline-none focus:border-[#0050B2] transition-colors" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-[#001F4E] mb-1.5">Email Address <span className="text-red-500">*</span></label>
                      <input name="email" required type="email" placeholder="john@company.com" className="w-full px-4 py-2.5 rounded-md border border-white bg-white text-sm text-[#001F4E] placeholder-gray-400 focus:outline-none focus:border-[#0050B2] transition-colors" />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-[#001F4E] mb-1.5">Phone Number</label>
                      <input name="phone" type="tel" placeholder="+1 (000) 000-0000" className="w-full px-4 py-2.5 rounded-md border border-white bg-white text-sm text-[#001F4E] placeholder-gray-400 focus:outline-none focus:border-[#0050B2] transition-colors" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-[#001F4E] mb-1.5">Company</label>
                      <input name="company" type="text" placeholder="Your Company" className="w-full px-4 py-2.5 rounded-md border border-white bg-white text-sm text-[#001F4E] placeholder-gray-400 focus:outline-none focus:border-[#0050B2] transition-colors" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-[#001F4E] mb-1.5">
                      Message <span className="text-red-500">*</span>
                      <span className={`ml-2 font-normal ${charCount > 500 ? 'text-red-500' : 'text-gray-400'}`}>({charCount}/500)</span>
                    </label>
                    <textarea
                      name="message"
                      required
                      rows={5}
                      maxLength={500}
                      onChange={(e) => setCharCount(e.target.value.length)}
                      placeholder="Tell us about your project or inquiry..."
                      className="w-full px-4 py-2.5 rounded-md border border-white bg-white text-sm text-[#001F4E] placeholder-gray-400 focus:outline-none focus:border-[#0050B2] transition-colors resize-none"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={submitting || charCount > 500}
                    className="w-full py-3 rounded-md bg-[#0050B2] text-white font-semibold text-sm hover:bg-[#001F4E] transition-colors duration-200 cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {submitting ? 'Sending...' : 'Send Message'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
